import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CacheBuster from 'react-cache-buster';
import "./App.css";
import { CDCHeader, CDCBody, CDCFooter } from "@cdcent/templatepackage-react";
import "@cdcent/templatepackage-react/assets/css/bootstrap.min.css";
import "@cdcent/templatepackage-react/assets/css/app.min.css";
import "@cdcent/templatepackage-react/assets/css/theme-teal.min.css";
import TopFilter from "./components/TopFilter";
import DataVisualizationContainer from "./components/DataVisualizationContainer";
import CardSection from "./components/CardSection";
import InfoDownloadSection from "./components/InfoDownloadSection";
import ComparisonReport from "./components/ComparisonReport";
import ScrollToTop from "./util/ScrollToTop";
import * as analytics from "./util/Analytics/HomeAnalytics";
import { configureAdobeLaunchMetrics, do_triggerPageView } from "./util/Analytics/Analytics_Custom";
import BreadCrumbAndMenu from "./components/BreadCrumbAndMenu";
import { GetTopics, GetLocations } from "./util/Api";
import {
    validateAndOpenUrl
} from "./util/constants";
const { version } = require('../package.json')
const App = () => {
    let navigate = useNavigate();

    const [firstRender, setFirstRender] = useState(true);
    useEffect(() => {

        if (firstRender) {

            //adobe analytics configuration
            //channels, levels, etc.
            configureAdobeLaunchMetrics()

            //fire the initial home pageview so Adobe gets a hit on first load.
            //(trigger_pageview internally queues until Launch finishes loading.)
            do_triggerPageView("DPH CDI");

            PageInit();

            setFirstRender(false);
        }
    }, [firstRender])


    const [errorLoadingData, setErrorLoadingData] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);

    const [locations, setLocations] = useState([]);
    const [topics, setTopics] = useState([]);

    async function handleResetIndicatorParameters() {
        await setIndicatorParameters([]);
    }
    async function handleResetSideFilterParameters() {
        await setSideFilterParameters([]);
    }
    async function PageInit() {
        // configureAdobeLaunchMetrics() is already called in the firstRender useEffect above,
        // so we don't call it again here (was being invoked twice on first load).

        let topicsData = [];
        let locationsData = [];

        await setDataLoading(true);

        await Promise.all([
            (async () => {
                topicsData = await fetchTopics();
                locationsData = await fetchLocations();
            })(),
        ]);


        if (!topicsData || !locationsData || topicsData.length === 0 || locationsData.length === 0) {
            //clear and set error 
            await setErrorLoadingData(true);
            await setTopics([]);
            await setLocations([]);
            await setDataLoading(false);
        } else {
            //set location and topics data
            await setLocations(locationsData);
            await setTopics(topicsData);

            //clear data loading and error (just incase)
            await setDataLoading(false);
            await setErrorLoadingData(false);

            //get query string params
            const urlParameters = new URLSearchParams(window.location.search);
            if (urlParameters && urlParameters.size > 0) {

                let locationParameter = null, categoryParameter = null, indicatorParameters = null, dataVizParameter = null,
                    viewmaps = null, viewgraphs = null, viewtrends = null, dataValueTypeIdParameter = null,
                    stratificationCategoryIdParameter = null, yearIdParameter = null;

                urlParameters.forEach((value, key) => {


                    if (key.toLowerCase() === "location") {
                        locationParameter = value;
                    }
                    if (key.toLowerCase() === "category") {
                        categoryParameter = value;
                    }
                    if (key.toLowerCase() === "indicators") {
                        indicatorParameters = value;
                    }
                    if (key.toLowerCase() === "visualization") {
                        dataVizParameter = value;
                    }
                    if (key.toLowerCase() === "viewmaps") {
                        viewmaps = value;
                    }
                    if (key.toLowerCase() === "viewgraphs") {
                        viewgraphs = value;
                    }
                    if (key.toLowerCase() === "viewtrends") {
                        viewtrends = value;
                    }
                    if (key.toLowerCase() === "datavaluetypeid") {
                        dataValueTypeIdParameter = value;
                    }
                    if (key.toLowerCase() === "stratificationcategoryid") {
                        stratificationCategoryIdParameter = value;
                    }
                    if (key.toLowerCase() === "yearid") {
                        yearIdParameter = value;
                    }
                });


                //process query string params
                await processParametersForPageLoad({
                    locationParameter,
                    categoryParameter,
                    indicatorParameters,
                    viewmaps,
                    viewgraphs,
                    viewtrends,
                    dataVizParameter,
                    dataValueTypeIdParameter, stratificationCategoryIdParameter, yearIdParameter, locationsData
                });
            }
        }
    }
    async function processParametersForPageLoad({
        locationParameter,
        categoryParameter,
        indicatorParameters,
        dataVizParameter,
        dataValueTypeIdParameter, stratificationCategoryIdParameter, yearIdParameter, locationsData, viewtrends
    }) {
        await setShowingComparisonReport(false);
        await setSelectedCards([]);
        await setTableData([]);

        if (dataVizParameter === "comparison-report") {
            do_triggerPageView("DPH CDI Comparison Report");

            locationParameter = locationParameter.replace("ALL", "US");
            await setLocationParameter({ LocationAbbr: locationParameter });
            await setShowingComparisonReport(true);
        } else {
            //more refinement needed

            const location = locationParameter === "ALL" ? locationsData : locationsData.filter(x => x.LocationAbbr === locationParameter)
            const locationDesc = location !== null && location.length > 0 ? location[0].LocationDesc : null

            await setLocationParameter({ LocationAbbr: locationParameter });
            await setCategoryParameter({ TopicId: categoryParameter });
            await setTopFilterSelection({
                location: { LocationAbbr: locationParameter, LocationDesc: locationDesc },
                topic: { TopicId: categoryParameter },
            });



            const list = indicatorParameters ? indicatorParameters.split(',') : null;

            if ((list && list.length === 1) || (viewtrends && viewtrends !== null)) {
                const sideFilterParameters = { dataValueTypeIdParameter, stratificationCategoryIdParameter, yearIdParameter, viewtrends }
                await setSideFilterParameters(sideFilterParameters);
            }

            await setIndicatorParameters(indicatorParameters);



        }
    }

    const [showingComparisonReport, setShowingComparisonReport] = useState(false);
    const [locationParameter, setLocationParameter] = useState(null);
    const [categoryParameter, setCategoryParameter] = useState(null);
    const [indicatorParameters, setIndicatorParameters] = useState([]);
    const [sideFilterParameters, setSideFilterParameters] = useState(null);
    const [topFilterSelection, setTopFilterSelection] = useState({
        location: null,
        topic: null,
    });
    const [selectedCards, setSelectedCards] = useState([]);
    const [cardsToSelect, setCardsToSelect] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [navSelected, setNavSelected] = useState("");


    async function fetchTopics() {
        const response = await GetTopics();
        return response && response.data ? response.data : null;
    }
    async function fetchLocations() {
        const response = await GetLocations();
        return response && response.data ? response.data : null;
    }

    async function handleViewResultsclick(nextTopFilterSelection) {
        await setSelectedCards([]);
        await setTopFilterSelection(nextTopFilterSelection);
        await setTableData([]);

        if (
            nextTopFilterSelection &&
            nextTopFilterSelection.location != null &&
            nextTopFilterSelection.topic.TopicId != null
        ) {
            navigate(
                "/?location=" +
                nextTopFilterSelection.location.LocationAbbr +
                "&category=" +
                nextTopFilterSelection.topic.TopicId
            );
        } else {
            navigate("/");
        }
    }

    async function handleIndicatorSubmitClick(selectedCards) {
        await setSelectedCards([]);
        await setSelectedCards(selectedCards);
        await setTableData([]);
        if (selectedCards && selectedCards.length > 0) {
            let indicators = [];
            selectedCards.map((question, i) => (
                indicators.push(question.QuestionId)
            ));

            navigate(
                "/?location=" +
                topFilterSelection.location.LocationAbbr +
                "&category=" +
                topFilterSelection.topic.TopicId +
                "&indicators=" +
                indicators.join(",")
            );

        } else {
            //navigate(
            //    "/?location=" +
            //    topFilterSelection.location.LocationAbbr +
            //    "&category=" +
            //    topFilterSelection.topic.TopicId)
            //
            //setCardsToSelect([])
            //alert("do we need this");

            setSelectedCards([]);


        }
    }

    const sideFilterYearRef = useRef(null);
    //very important logic here. careful with changes
    async function handleNavigateToMapAllLocations(
        selectedCard,
        topic,
        sideFilterSelection
    ) {
        let selectedYear = sideFilterSelection.YearId;
        if (sideFilterSelection.YearId === "ALLYEARS") {
            selectedYear =
                sideFilterYearRef.current.querySelectorAll("input")[1].value;
        }
        window.location =
            "?location=ALL&category=" +
            topic.TopicId +
            "&indicators=" +
            selectedCard.QuestionId +
            "&DataValueTypeId=" +
            sideFilterSelection.DataValueTypeId +
            "&StratificationCategoryId=" +
            sideFilterSelection.StratificationCategoryId +
            "&YearId=" +
            selectedYear;

        analytics.selectViewMapLink();
    }

    function handleViewComparisonReport() {
        do_triggerPageView("DPH CDI Comparison Report");
        analytics.selectTopNavButton({ topNavButton: "Comparison Report" });
        setNavSelected("comparison");
        navigate("?visualization=comparison-report&location=US");
        setShowingComparisonReport(true);
    }

    const handleLinkButtonRedirect = (url, newWindow = false) => {
        validateAndOpenUrl(url, newWindow);
    };

    function handleMenuClick() {
        const dropdownMenu = document.querySelector(".nav-dropdown");
        dropdownMenu.classList.toggle("hidden");
    }

    async function handleTableDataChange(data) {
        await setTableData((tableData) => [
            ...tableData.filter((item) => item.QuestionId !== data.QuestionId),
            data,
        ]);
    }

    const OkToShowCards =
        topFilterSelection &&
            topFilterSelection.location &&
            topFilterSelection.location.LocationAbbr &&
            topFilterSelection.topic &&
            topFilterSelection.topic.TopicId
            ? true
            : false;

    const OkToShowDataViz =
        OkToShowCards && selectedCards && selectedCards.length > 0 ? true : false;
    const comparisonSelected =
        navSelected && navSelected === "comparison" ? "true" : "false";

    const hasTopicsAndLocations = !topics || !locations || topics.length === 0 || locations.length === 0 ? false : true;

    const isProduction = process.env.NODE_ENV === 'production';
    return (
        <CacheBuster
            currentVersion={version}
            isEnabled={isProduction} //If false, the library is disabled.
            isVerboseMode={false} //If true, the library writes verbose logs to console.
            loadingComponent={<div>loading</div>} //If not pass, nothing appears at the time of new version check.
            metaFileDirectory={'.'} //If public assets are hosted somewhere other than root on your server.
        >
            <div style={{ backgroundColor: "rgb(245, 245, 245)" }}>
                <CDCHeader link={process.env.REACT_APP_CDC_BASE_URL + "/cdi"} search={false} title={<h1>Chronic Disease Indicators</h1>} ></CDCHeader>
                <BreadCrumbAndMenu
                    handleViewComparisonReport={handleViewComparisonReport}
                    handleLinkButtonRedirect={handleLinkButtonRedirect}
                    handleMenuClick={handleMenuClick}
                    comparisonSelected={comparisonSelected}
                />
                <CDCBody>
                    {errorLoadingData &&
                        < div className="alert alert-danger" role="alert">The application was unable to load data. Please refresh the page and try again.</div>
                    }
                    {dataLoading &&
                        <span>loading</span>
                    }
                    {hasTopicsAndLocations &&
                        <>

                            {showingComparisonReport ? (
                                <ComparisonReport locationParameter={locationParameter} />
                            ) : (
                                <>

                                    <TopFilter
                                        locationParameter={locationParameter}
                                        categoryParameter={categoryParameter}
                                        handleViewResultsclick={handleViewResultsclick}
                                        topics={topics}
                                        locations={locations}
                                    />


                                    {OkToShowCards && (
                                        <>
                                            <CardSection
                                                topFilterSelection={topFilterSelection}
                                                cardsToSelect={cardsToSelect}
                                                indicatorParameters={indicatorParameters}
                                                submitClick={handleIndicatorSubmitClick}
                                                resetCardsToSelect={setCardsToSelect}
                                                resetIndicatorParameters={handleResetIndicatorParameters}
                                            />
                                        </>
                                    )}
                                    <div className="container">
                                        <div className="row">
                                            {OkToShowDataViz && (
                                                <>
                                                    <InfoDownloadSection
                                                        tableData={tableData}
                                                        topFilterSelection={topFilterSelection}
                                                        selectedCards={selectedCards}
                                                    ></InfoDownloadSection>
                                                    <DataVisualizationContainer
                                                        topFilterSelection={topFilterSelection}
                                                        selectedCards={selectedCards}
                                                        onTableDataChange={handleTableDataChange}
                                                        onNavigateToMapAllLocations={
                                                            handleNavigateToMapAllLocations
                                                        }
                                                        sideFilterParameters={sideFilterParameters}
                                                        onResetSideFilterParameters={handleResetSideFilterParameters}
                                                        sideFilterYearRef={sideFilterYearRef}

                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                            <ScrollToTop />
                        </>
                    }

                </CDCBody>
                <div className="d-print-none">
                    <CDCFooter></CDCFooter>
                </div>
            </div>
        </CacheBuster>
    );
};

export default App;